import React, { useEffect, useRef, useState } from "react";
import Table from "../../../../components/common/Table/Table";
import Dropdown from "../../../../components/common/AdminDropdown";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import {
  clearApplications,
  fetchAdminApplications,
  setCurrentPage,
  updateApplicationStatus,
} from "../../../../store/slices/Citizenship/adminSlice";
import Pagination from "../../../../components/common/Table/Pagination";
import Loader from "../../../../components/common/Loader";
import Notifications from "../../../../components/common/Notifications";
import { trackButtonClick } from '../../../../services/analytics';
import UploadErrorBanner from "../../UploadDocument/ViewDetails/UploadErrorBanner";

interface DetailsTableProps {
  selectedStatus: string;
  onViewDetails: (rowData: any) => void;
}

interface TableColumn {
  key: string;
  label: string | React.ReactNode;
  className?: string;
}
const defaultCellClass = "align-middle!"
const DetailsTable = ({ selectedStatus, onViewDetails }: DetailsTableProps) => {
  const dispatch = useAppDispatch();
  const isStatusChangingRef = useRef(false);

  const { status, applications, loading, currentPage, perPage, totalItems } =
    useAppSelector((state) => state.admin);

  const [selectAll, setSelectAll] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [localSelectedStatus, setLocalSelectedStatus] =
    useState<string>("Choose status");
  const [activeRowId, setActiveRowId] = useState<number | null>(null);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [showErrorBanner, setShowErrorBanner] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Mark status change
  useEffect(() => {
    isStatusChangingRef.current = true;
    dispatch(setCurrentPage(1));
  }, [selectedStatus, dispatch]);

  /* -------------------- FETCH APPLICATIONS -------------------- */
  useEffect(() => {
    if (isStatusChangingRef.current && currentPage !== 1) {
      return; // wait until page resets
    }

    isStatusChangingRef.current = false;

    dispatch(
      fetchAdminApplications({
        page: currentPage,
        perPage,
        status: selectedStatus,
      })
    );
  }, [currentPage, perPage, selectedStatus, dispatch]);

  /* Reset selection when page or filter changes */
  useEffect(() => {
    dispatch(clearApplications());
    setSelectedIds([]);
    setSelectAll(false);
        // Reset active row when status changes
    setActiveRowId(null);
    setShowErrorBanner(false); 
  }, [currentPage, selectedStatus]);

  /* -------------------- TABLE COLUMNS -------------------- */
  const columns: TableColumn[] = [
    {
      key: "checkbox",
      label: (
        <div className="flex items-center justify-center py-2! h-full">
          <input
            type="checkbox"
            checked={selectAll}
            className="scale-75 cursor-pointer"
            onChange={(e) => handleSelectAll(e.target.checked)}
          />
        </div>
      ),
      className: "text-center px-6 w-15 align-middle",
    },
  { key: "empId", label: "Employee ID", className: defaultCellClass },
  { key: "nuid", label: "Employee NUID", className: defaultCellClass },
  { key: "empLastName", label: "Last name", className: defaultCellClass },
  { key: "empFirstName", label: "First name", className: defaultCellClass },
  { key: "applictnId", label: "Application ID", className: defaultCellClass },
  { key: "applictnDate", label: "Application Date", className: defaultCellClass },
  { key: "status", label: "Status", className: defaultCellClass },
  { key: "action", label: "Action", className: defaultCellClass },
  ];

  /* -------------------- HANDLERS -------------------- */
  const handleStatusSelect = (value: string) => {
    setLocalSelectedStatus(value);
    if (value !== "Choose status") {
    setShowErrorBanner(false);  // ← ADD THIS - Hide error when valid status selected
  }
  };
  const handleSelectAll = (isChecked: boolean) => {
    setSelectAll(isChecked);

    if (isChecked && applications?.items) {
      setSelectedIds(applications.items.map((app: any) => app.applictnId));
    } else {
      setSelectedIds([]);
      setShowErrorBanner(false);
    }
  };

  const handleRowCheckboxChange = (app: any, isChecked: boolean) => {
    setSelectedIds((prev) => {
      const newSelected = isChecked
        ? [...prev, app.applictnId]
        : prev.filter((id) => id !== app.applictnId);

      // Uncheck "Select All" if not all rows are selected
      if (
        applications?.items &&
        newSelected.length < applications.items.length
      ) {
        setSelectAll(false);
      } else if (
        applications?.items &&
        newSelected.length === applications.items.length
      ) {
        setSelectAll(true);
      }
      if (newSelected.length === 0 && !isChecked ) {
        setShowErrorBanner(false);
      }

      return newSelected;
    });
  };

  const handleApplyChanges = async () => {
    if (selectedIds.length === 0 || localSelectedStatus === "Choose status") {
      setErrorMessage("Please select applications and a valid status.");
      setShowErrorBanner(true);
      return;
    }
    console.log(`📊 Analytics: Apply status change - ${localSelectedStatus} for ${selectedIds.length} applications`);
    trackButtonClick('Apply changes', 'citizenship-details-table', { newStatus: localSelectedStatus, count: selectedIds.length });

    try {
      await dispatch(
        updateApplicationStatus({
          applicationIds: selectedIds.map(String),
          status: localSelectedStatus,
        })
      ).unwrap();

      // Status update succeeded — show success and reset selection immediately
      setShowErrorBanner(false);
      setSelectedIds([]);
      setSelectAll(false);
      setLocalSelectedStatus('Choose status');
      setShowSuccessBanner(true);
      setTimeout(() => {
        setShowSuccessBanner(false);
      }, 5000);

      // Re-fetch the table. If we were on the last page and it's now empty,
      // fall back to the new last page so the user sees remaining results.
      dispatch({ type: "admin/clearApplications" });
      try {
        const result = await dispatch(
          fetchAdminApplications({
            page: currentPage,
            perPage,
            status: selectedStatus,
          })
        ).unwrap();
        // Current page is now beyond the available data — jump to the new last page
        if (currentPage > 1 && (!result?.items?.length)) {
          const newLastPage = result?.meta?.totalPages ?? (currentPage - 1);
          dispatch(setCurrentPage(Math.max(1, newLastPage)));
        }
      } catch {
        // Re-fetch failed (e.g. page no longer exists) — fall back to previous page
        if (currentPage > 1) {
          dispatch(setCurrentPage(currentPage - 1));
        }
      }
    } catch {
      dispatch({ type: "admin/clearApplications" });
      setErrorMessage("Failed to update status. Please try again.");
      setShowErrorBanner(true);
    }
  };

  const handlePageChange = (page: number) => {
    dispatch(setCurrentPage(page));
  };

  /* -------------------- ROWS -------------------- */
  const rows =
    !loading && applications?.items?.length
      ? applications.items.map((app: any) => ({
          checkbox: (
            <div className="flex items-center justify-center h-full py-5!">
              <input
                type="checkbox"
                checked={selectedIds.includes(app.applictnId)}
                className="scale-75 cursor-pointer"
                onChange={(e) => handleRowCheckboxChange(app, e.target.checked)}
              />
            </div>
          ),
          empId: app.empId,
          nuid: app.nuid,
          empLastName: app.empLastName,
          empFirstName: app.empFirstName,
          applictnId: app.applictnId,
          applictnDate: app.applictnDate.replaceAll('-','/'),
          status: (
      <span className="bg-gray-100 border border-[#00000014] rounded px-1! py-1! text-sm font-semibold inline-flex items-center justify-center">
        {app.status.charAt(0).toUpperCase()+app.status.slice(1).toLowerCase()}
      </span>
    ),
          action: (
            <button
              className={`button ${
                activeRowId === app.applictnId
                  ? "bg-[#003B71]! text-white!"
                  : "-primary"
              }`}
              onClick={() => {
                console.log(`📊 Analytics: View details clicked - Application ${app.applictnId}`);
                trackButtonClick('View details', 'citizenship-details-table', { applicationId: app.applictnId });
                // trackCustomPageView(`Citizenship Application Details - ${app.applictnId}`);
                setActiveRowId(app.applictnId);
                onViewDetails(app);
              }}
            >
              View details
            </button>
          ),
        }))
      : [];

  /* -------------------- RENDER -------------------- */
  return (
    <div className="lg:px-40! mt-8">
      {/* Success Banner */}
      {showSuccessBanner && (
        <Notifications
          messageInfo="Status updated successfully."
          status={true}
        />
      )}

      {/* Error Banner */}
      {showErrorBanner && (
        <UploadErrorBanner
          header = "Error"
          message={errorMessage}
          
        />
      )}
      
      <div className="flex items-end gap-2 px-4 pb-4">
        {rows.length > 0 && (
          <>
            {/** Enabled only when at least one checkbox is selected */}
            {(() => {
              const hasSelection = selectedIds.length > 0;
              return (
                <>
                  <Dropdown
                    label="Change status for selected items:"
                    selectedValue={localSelectedStatus}
                    options={status}
                    onSelect={handleStatusSelect}
                    className={`${hasSelection ? '' : 'pointer-events-none opacity-50'} ml-4!`}
                    btnClassName={`min-w-[15rem] text-left`}
                  />
                  <button
                    className={`button -primary min-w-[180px]! ${hasSelection ? '' : 'opacity-50 cursor-not-allowed'}`}
                    onClick={handleApplyChanges}
                    disabled={!hasSelection}
                  >
                    Apply changes
                  </button>
                </>
              );
            })()}
          </>
        )}
      </div>

      <div className="mt-8!">
        {/*  Loading */}
        {loading && (
          <div className="text-center py-10 text-gray-500">
            <Loader />
          </div>
        )}

        {/*  Empty State */}
        {!loading && rows.length === 0 && (
          <div className="text-center py-10">
            No applications found for the selected status.
          </div>
        )}

        {/* Table */}
        {!loading && rows.length > 0 && (
          <>
            <Table columns={columns as any} rows={rows} />

            <div className="mt-4 mobile:relative! mobile:left-[30px]">
              <Pagination
                totalItems={totalItems || applications.items.length}
                itemsPerPage={perPage}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DetailsTable;
